import { isNil } from '@/utils/helpers'
import useLocalStorage from '@/utils/localStorage'
import translations from '@/utils/localization'
import { useSearchParams } from 'next/navigation'
import { FunctionComponent, useContext, createContext, useEffect } from 'react'
import { IntlProvider } from 'react-intl'

const languageContext = createContext<LanguageContext>({
  locale: 'mn',
  setLocale: (locale) => {
    throw new Error('setLocale not implemented')
  }
})

export const LanguageProvider: FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocale] = useLocalStorage<Locale>('@mobicom_locale', 'mn')
  let localeToUse = (!isNil(locale) && (locale === 'mn' || locale === 'en')) ? locale : 'mn'

  const lang = useSearchParams().get('lang')

  useEffect(() => {
    if (lang !== null && lang.trim() !== '') {
      localeToUse = lang as Locale
      setLocale(lang as Locale)
    }
  }, [lang])

  return (
    <languageContext.Provider
      value={{
        locale: localeToUse,
        setLocale
      }}
    >
      <IntlProvider locale='en-US' messages={translations[localeToUse]}>
        {children}
      </IntlProvider>
    </languageContext.Provider>
  )
}

export const useLanguage = (): LanguageContext => {
  return useContext(languageContext)
}

export const supportedLanguages: Record<Locale, Language> = {
  mn: {
    locale: 'mn',
    name: 'MN',
    icon: '/assets/imgs/mn.png'
  },
  en: {
    locale: 'en',
    name: 'EN',
    icon: '/assets/imgs/en.png'
  }
}

export interface LanguageContext {
  locale: Locale
  setLocale: (locale: Locale) => void
}

export type Locale = 'en' | 'mn'

export interface Language {
  locale: Locale
  name: string
  icon: string
}
